import { formatRelativeTime } from "@/lib/relativeTime";
import { Button } from "@simplicity/ui";
import { Archive, Copy, Pencil, Trash2 } from "lucide-react";

import { type DashboardRow } from "./types";

export function DashboardViewRow({
  d,
  onStartEdit,
  onArchiveToggle,
  onDelete,
  onDuplicate,
}: {
  d: DashboardRow;
  onStartEdit: (d: DashboardRow) => void;
  onArchiveToggle: (id: string, archived: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate?: () => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <span className="font-medium">{d.title}</span>
        {d.archived && (
          <span className="ml-2 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
            Archived
          </span>
        )}
        <div className="text-xs text-muted-foreground mt-0.5">
          {d.school_year} &middot; {d.category}
          {d.viewCount != null && (
            <span className="ml-2">
              &middot; {d.viewCount} {d.viewCount === 1 ? "view" : "views"}
            </span>
          )}
          {d.lastViewedAt !== undefined && (
            <span
              className="ml-2"
              title={d.lastViewedAt ? new Date(d.lastViewedAt).toLocaleString() : "Never"}
            >
              &middot; Last viewed: {formatRelativeTime(d.lastViewedAt ?? null)}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-auto p-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => onStartEdit(d)}
          title="Edit"
          aria-label="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        {d.type === "spark" && onDuplicate && (
          <Button
            variant="ghost"
            size="icon"
            className="h-auto p-1.5 text-muted-foreground hover:text-foreground"
            onClick={onDuplicate}
            title="Duplicate"
            aria-label="Duplicate"
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-auto p-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => onArchiveToggle(d.id, d.archived)}
          title={d.archived ? "Unarchive" : "Archive"}
          aria-label={d.archived ? "Unarchive" : "Archive"}
        >
          <Archive className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-auto p-1.5 text-muted-foreground hover:text-destructive"
          onClick={() => onDelete(d.id)}
          title="Delete"
          aria-label="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
